import { Controller } from '@hotwired/stimulus';

// Where the v6 nav swaps the desktop row for the drawer. Wider than Bootstrap's
// lg (992px), so keep it in step with the v6 stylesheets and the
// nav-menu-desktop-min-width value on the v6 nav.
const DESKTOP_QUERY = '(min-width: 1121px)';

// Expands the Shop Recipes panel on click (not hover) and repaints the header
// while it is open. Declared on the header, so the toggle and panel inside the
// nav partial reach it by action bubbling.
export default class extends Controller {
  static targets = ['toggle', 'panel'];
  static values = { openClass: { type: String, default: 'is-recipes-open' } };

  connect() {
    // One AbortController, so disconnect removes the exact listeners it added.
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    document.addEventListener('click', this.closeOnOutsideClick.bind(this), { signal });
    document.addEventListener('keydown', this.closeOnEscape.bind(this), { signal });

    // The panel is desktop-only; below the breakpoint the stylesheet hides it,
    // but the open class would linger and leave the header green with the dark
    // hamburger invisible against it. Listen on the query, not on resize, so it
    // fires once per crossing and needs no throttle.
    this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
    this.desktopQuery.addEventListener('change', this.closeBelowDesktop.bind(this), { signal });
  }

  disconnect() {
    this.abortController?.abort();
  }

  get isOpen() {
    return this.element.classList.contains(this.openClassValue);
  }

  toggle(event) {
    event.preventDefault();

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.element.classList.add(this.openClassValue);
    this.toggleTargets.forEach((el) => el.setAttribute('aria-expanded', 'true'));
  }

  close() {
    if (!this.isOpen) return;

    this.element.classList.remove(this.openClassValue);
    this.toggleTargets.forEach((el) => el.setAttribute('aria-expanded', 'false'));
  }

  closeBelowDesktop() {
    if (this.desktopQuery.matches) return;

    this.close();
  }

  closeOnOutsideClick(event) {
    if (this.element.contains(event.target)) return;

    this.close();
  }

  closeOnEscape(event) {
    if (event.key !== 'Escape' || !this.isOpen) return;

    this.close();
    // hasToggleTarget, not optional chaining: a missing Stimulus target throws.
    if (this.hasToggleTarget) this.toggleTarget.focus();
  }
}
